import type { MethodsDraft } from './types';

// #496 (review r2 #1): the choices of the preferences step (return / refund
// method, the edited pickup address, the bank details, the agreement) kept
// while the customer goes back to correct the products step. They live in
// this module's memory only, never in history state (the browser may persist
// it to disk): history carries the random draft id. A reload or a new tab has
// no draft, so the preferences step starts fresh. Cleared on a successful
// submission, when the flow starts again (the lookup form) and on a shop
// change (`setShop`); a read is also scoped to its shop and order.

export interface MethodsDraftScope {
  shopId: number;
  orderId: number;
}

const drafts = new Map<
  string,
  { scope: MethodsDraftScope; draft: MethodsDraft }
>();

const randomId = (): string => {
  const cryptoApi = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  if (cryptoApi?.getRandomValues) {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

export const saveMethodsDraft = (
  draft: MethodsDraft,
  scope: MethodsDraftScope
): string => {
  const id = randomId();
  drafts.set(id, { scope: { ...scope }, draft });
  return id;
};

export const readMethodsDraft = (
  id: string | undefined,
  scope: MethodsDraftScope
): MethodsDraft | undefined => {
  if (id === undefined) return undefined;
  const entry = drafts.get(id);
  if (
    !entry ||
    entry.scope.shopId !== scope.shopId ||
    entry.scope.orderId !== scope.orderId
  )
    return undefined;
  return entry.draft;
};

export const hasMethodsDraft = (id: string): boolean => drafts.has(id);

export const clearMethodsDrafts = (): void => drafts.clear();
